import { useEffect, useMemo, useState } from 'react'
import { useOnboardingStore } from '../stores/onboardingStore'
import { StepSubmitProvider } from '../context/StepSubmitContext'
import Step2Billing from '../steps/Step2Billing'
import Step3ManagedBilling from '../steps/Step3ManagedBilling'
import Step6NetworkPlus from '../steps/Step6NetworkPlus'

const STEP2_BUCKETS = [
  'standard',
  'small_practice',
  'trial_then_99',
  'trial_then_174',
  'permanent_waiver',
]

const STEP3_FEES = ['2.99', '1.99', '0.99', '0']

const STEP5_VARIANTS = [
  { label: 'none', query: 'coupon=none' },
  { label: 'manual', query: 'coupon=manual' },
  { label: 'auto · 50%', query: 'coupon=auto&pct=50' },
  { label: 'auto · 100%', query: 'coupon=auto&pct=100' },
  { label: 'auto · 50% + bank', query: 'coupon=auto&pct=50&bank=true' },
]

function parsePath() {
  if (typeof window === 'undefined') return { step: null, params: {} }
  const path = window.location.pathname.replace(/\/+$/, '')
  const m = path.match(/^\/preview\/(step\d+)$/)
  if (!m) return { step: null, params: {} }
  const params = Object.fromEntries(
    new URLSearchParams(window.location.search).entries(),
  )
  return { step: m[1], params }
}

function setLocation(path, search) {
  const url = new URL(window.location.href)
  url.pathname = path
  url.search = search ? (search.startsWith('?') ? search : `?${search}`) : ''
  window.location.href = url.toString()
}

function seedStore({ step, params }) {
  const store = useOnboardingStore.getState()

  // Reset to a known-clean form state. We don't persist anything from the
  // preview run — localStorage stays whatever it was from the real flow.
  store.reset()
  store.setPersonalization(null)

  // All steps render against a minimum form payload so they don't crash.
  store.updateFormData('details', {
    practiceName: 'Preview Practice',
    logo: null,
  })

  if (step === 'step2') {
    const bucket = params.bucket || 'standard'
    store.setPersonalization({
      practiceName: 'Preview Practice',
      ownerFirstName: 'Sam',
      welcomeLine: '',
      internalNote: '',
      discountBucket: bucket,
      hormoneDiscountPct: null,
      nplusPatientFeeDiscountPct: null,
      couponCode: null,
    })
    return
  }

  if (step === 'step3') {
    const feeRaw = params.fee
    const feeNum = feeRaw === undefined || feeRaw === '' ? null : Number(feeRaw)
    store.setPersonalization({
      practiceName: 'Preview Practice',
      ownerFirstName: 'Sam',
      welcomeLine: '',
      internalNote: '',
      discountBucket: 'standard',
      stripeProcessingFeePct: Number.isFinite(feeNum) ? feeNum : null,
      couponCode: null,
    })
    return
  }

  if (step === 'step5') {
    const coupon = params.coupon || 'none'
    const pct = Number(params.pct) || 0
    const bank = params.bank === 'true'

    // Step 5 reads stripeCustomerId from the store to enable several UI
    // states (the right-side N+ pricing card, payment buttons). Inject a
    // fake one so previews show the loaded UI instead of the blank state.
    store.updateFormData('stripe', { stripeCustomerId: 'cus_PREVIEW' })

    if (bank) {
      store.updateFormData('managedBilling', {
        achAccount: {
          accountId: 'fca_PREVIEW',
          bankName: 'Preview Bank',
          last4: '1234',
        },
      })
    }

    if (coupon === 'auto') {
      const code = `PREVIEW${pct}`
      store.setPersonalization({
        practiceName: 'Preview Practice',
        ownerFirstName: 'Sam',
        welcomeLine: '',
        internalNote: '',
        discountBucket: 'standard',
        couponCode: code,
      })
      const SETUP_CENTS = 499700
      const finalAmount = Math.max(0, Math.round(SETUP_CENTS * (1 - pct / 100)))
      const discountAmount = SETUP_CENTS - finalAmount
      store.setNetworkPlusAppliedPromotionCode({
        id: 'preview',
        code,
        label: pct === 100 ? 'Setup fee waived' : `${pct}% off`,
        discountAmount,
        finalAmount,
      })
    } else if (coupon === 'manual') {
      store.setPersonalization(null)
    } else {
      // 'none'
      store.setPersonalization(null)
    }

    // The N+ right-side pricing card only renders once the user has chosen
    // "Yes, enrol in Network+" — drive isMemberPlusMember=true so the card
    // is visible by default in preview.
    store.updateFormData('networkplus', { isMemberPlusMember: true })
    return
  }
}

function VariantSwitcher({ step, params }) {
  const linkStyle = (active) => ({
    padding: '4px 10px',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 500,
    textDecoration: 'none',
    color: active ? 'var(--accent)' : 'var(--text-2)',
    background: active ? 'rgba(38,161,136,0.15)' : 'rgba(255,255,255,0.04)',
    border: active
      ? '1px solid rgba(38,161,136,0.4)'
      : '1px solid rgba(255,255,255,0.08)',
    cursor: 'pointer',
  })

  return (
    <div
      style={{
        position: 'fixed',
        top: 12,
        right: 12,
        zIndex: 9999,
        background: 'rgba(0,0,0,0.85)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 10,
        padding: '12px 14px',
        backdropFilter: 'blur(12px)',
        fontFamily:
          'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial',
        maxWidth: 360,
      }}
    >
      <div
        style={{
          fontSize: 9,
          letterSpacing: '0.12em',
          color: 'var(--accent)',
          marginBottom: 8,
          fontWeight: 600,
          textTransform: 'uppercase',
        }}
      >
        Preview · {step}
      </div>

      {step === 'step2' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {STEP2_BUCKETS.map((b) => (
            <button
              key={b}
              type="button"
              style={linkStyle(params.bucket === b || (!params.bucket && b === 'standard'))}
              onClick={() => setLocation('/preview/step2', `bucket=${b}`)}
            >
              {b}
            </button>
          ))}
        </div>
      )}

      {step === 'step3' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {STEP3_FEES.map((f) => (
            <button
              key={f}
              type="button"
              style={linkStyle(
                (params.fee || '2.99') === f,
              )}
              onClick={() => setLocation('/preview/step3', `fee=${f}`)}
            >
              {f}%
            </button>
          ))}
        </div>
      )}

      {step === 'step5' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {STEP5_VARIANTS.map((v) => {
            const current = `${params.coupon || 'none'}${params.pct ? `&pct=${params.pct}` : ''}${params.bank ? '&bank=true' : ''}`
            const target = v.query
            return (
              <button
                key={v.label}
                type="button"
                style={linkStyle(current === target)}
                onClick={() => setLocation('/preview/step5', target)}
              >
                {v.label}
              </button>
            )
          })}
        </div>
      )}

      <div
        style={{
          fontSize: 10,
          color: 'var(--text-3)',
          marginTop: 10,
          paddingTop: 8,
          borderTop: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        Jump to:{' '}
        <a
          href="/preview/step2?bucket=trial_then_99"
          style={{ color: 'var(--text-2)' }}
        >
          step 2
        </a>
        {' · '}
        <a
          href="/preview/step3?fee=1.99"
          style={{ color: 'var(--text-2)' }}
        >
          step 3
        </a>
        {' · '}
        <a
          href="/preview/step5?coupon=auto&pct=50"
          style={{ color: 'var(--text-2)' }}
        >
          step 5
        </a>
      </div>
    </div>
  )
}

function PreviewIndex() {
  const link = (label, href) => (
    <a
      href={href}
      style={{
        display: 'block',
        padding: '10px 14px',
        marginBottom: 6,
        borderRadius: 8,
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        color: 'var(--text-1)',
        textDecoration: 'none',
        fontSize: 13,
      }}
    >
      {label}
    </a>
  )

  return (
    <div style={{ maxWidth: 720, margin: '60px auto', padding: 24 }}>
      <h1 style={{ fontSize: 24, marginBottom: 6 }}>Preview routes</h1>
      <p
        style={{
          color: 'var(--text-3)',
          fontSize: 13,
          marginBottom: 20,
        }}
      >
        Renders any step in isolation with a fake personalization payload.
      </p>

      <h3 style={{ fontSize: 14, marginTop: 24, marginBottom: 8 }}>
        Step 2 — Platform Fee Card
      </h3>
      {STEP2_BUCKETS.map((b) =>
        link(`step2 · ${b}`, `/preview/step2?bucket=${b}`),
      )}

      <h3 style={{ fontSize: 14, marginTop: 24, marginBottom: 8 }}>
        Step 3 — Stripe Processing Fee
      </h3>
      {STEP3_FEES.map((f) => link(`step3 · ${f}%`, `/preview/step3?fee=${f}`))}

      <h3 style={{ fontSize: 14, marginTop: 24, marginBottom: 8 }}>
        Step 5 — Network+ Coupon
      </h3>
      {STEP5_VARIANTS.map((v) =>
        link(`step5 · ${v.label}`, `/preview/step5?${v.query}`),
      )}
    </div>
  )
}

export default function PreviewPage() {
  const parsed = useMemo(() => parsePath(), [])
  const { step, params } = parsed
  const [seeded, setSeeded] = useState(false)

  useEffect(() => {
    if (!step) return
    seedStore(parsed)
    setSeeded(true)
  }, [step, parsed])

  if (!step) return <PreviewIndex />
  if (!seeded) return null

  let StepComponent = null
  if (step === 'step2') StepComponent = Step2Billing
  else if (step === 'step3') StepComponent = Step3ManagedBilling
  else if (step === 'step5') StepComponent = Step6NetworkPlus

  if (!StepComponent) return <PreviewIndex />

  return (
    <StepSubmitProvider>
      <div className="form-shell" style={{ minHeight: '100vh' }}>
        <main className="form-content">
          <div
            className="form-content-inner step-fit-viewport"
            style={{ paddingTop: 80 }}
          >
            <StepComponent />
          </div>
        </main>
        <VariantSwitcher step={step} params={params} />
      </div>
    </StepSubmitProvider>
  )
}
